import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Upload, Trash2, ImagePlus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from './shared';

/**
 * Modal that lets admins manually attach / remove photos on an existing
 * warranty. Photos are uploaded to Cloudinary via the backend and appended
 * to the warranty doc's `before_images` or `after_images` array. The bundled
 * invoice PDF (and standalone certificate) refresh automatically because the
 * backend invalidates the linked invoice's PDF cache on every change.
 */
const WarrantyPhotosModal = ({ warrantyId, onClose }) => {
  const { authHeader } = useAuth();
  const fileInputRef = useRef(null);
  const [stage, setStage] = useState('after');
  const [before, setBefore] = useState([]);
  const [after, setAfter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/warranty/${warrantyId}/photos`, {
        headers: authHeader(),
      });
      setBefore(data.before_images || []);
      setAfter(data.after_images || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [warrantyId, authHeader]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setErr('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('stage', stage);
      await axios.post(
        `${API}/billing/warranty/${warrantyId}/photos`,
        fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      await fetchPhotos();
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (url, photoStage) => {
    if (!window.confirm('Remove this photo from the warranty? This also removes it from the bundled PDF and the public verify page.')) return;
    try {
      await axios.post(
        `${API}/billing/warranty/${warrantyId}/photos/delete`,
        { url, stage: photoStage },
        { headers: authHeader() },
      );
      await fetchPhotos();
    } catch (ex) {
      setErr(formatErr(ex));
    }
  };

  const Gallery = ({ title, photos, photoStage, accent }) => (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className={`text-xs uppercase tracking-wider font-semibold ${accent}`}>{title}</h3>
        <span className="text-xs text-slate-500">{photos.length} photo{photos.length === 1 ? '' : 's'}</span>
      </div>
      {photos.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3">No {photoStage}-install photos yet.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((url) => (
            <div key={url} className="relative group aspect-square rounded overflow-hidden border border-slate-200">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => handleDelete(url, photoStage)}
                className="absolute top-1 right-1 p-1 bg-red-600/90 hover:bg-red-700 text-white rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove photo"
                data-testid={`warranty-photo-delete-${url.slice(-20)}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="warranty-photos-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ImagePlus size={16} className="text-teal-700" />
            <h2 className="text-sm font-semibold text-slate-900">
              Warranty Photos — <span className="font-mono">{warrantyId}</span>
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="warranty-photos-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <p className="text-xs text-slate-500">
            Photos attached here appear on the warranty certificate page bundled inside the invoice PDF
            and on the public eWarranty verification page. JPEG / PNG only, max 25 MB per image.
          </p>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="warranty-photos-error">
              {err}
            </div>
          )}

          {/* Upload control */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-xs uppercase tracking-wider text-teal-800 font-semibold whitespace-nowrap">
                Add as
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className={`${inputCls} max-w-[200px]`}
                data-testid="warranty-photos-stage"
              >
                <option value="after">After installation</option>
                <option value="before">Before installation</option>
              </select>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 text-sm bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg"
                data-testid="warranty-photos-upload-btn"
              >
                {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                {uploading ? 'Uploading…' : 'Choose photos'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleUpload}
                className="hidden"
                data-testid="warranty-photos-file-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-6 text-slate-500">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Loading photos…
            </div>
          ) : (
            <div className="space-y-3">
              <Gallery title="After installation" photos={after} photoStage="after" accent="text-emerald-700" />
              <Gallery title="Before installation" photos={before} photoStage="before" accent="text-slate-600" />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200"
            data-testid="warranty-photos-done-btn"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPhotosModal;
